const DEFAULT_BASELINE_LABELS = {
  all: "everything shown in this report",
  day: "this same day",
  employee: "this employee's total (in this report)",
};

const GROUP_BASELINES = {
  employee: "all",
  employee_day: "day",
  employee_category: "employee",
  employee_item: "employee",
  employee_payment: "employee",
};

const getBaselineLabels = (mixMeta) => ({
  ...DEFAULT_BASELINE_LABELS,
  ...(mixMeta?.baseline_labels || {}),
});

const COMMON_FILTER_TOOLTIPS = {
  start_date: "First day included (salon timezone).",
  end_date: "Last day included (salon timezone).",
  employee: "Choose who to include in the results.",
  staff: "Choose who to include in the results.",
  category: "Filter results to a category.",
  search: "Type a keyword to narrow results.",
  include_refunds: "Includes this item in totals and rows.",
  include_discounts: "Includes this item in totals and rows.",
  include_taxes: "Includes this item in totals and rows.",
  include_tips: "Includes this item in totals and rows.",
};

export const SALES_BY_EMPLOYEE_PCT_TOOLTIPS = {
  net_pct: (mixMeta) => {
    const baseline =
      getBaselineLabels(mixMeta)[
        SALES_BY_EMPLOYEE_PCT_TOOLTIPS.baseline_key(mixMeta)
      ] || "this report";
    return `This is this row's share of net sales compared to ${baseline}.`;
  },
  tickets_pct: (mixMeta) => {
    const baseline =
      getBaselineLabels(mixMeta)[
        SALES_BY_EMPLOYEE_PCT_TOOLTIPS.baseline_key(mixMeta)
      ] || "this report";
    return `This is this row's share of tickets compared to ${baseline}.`;
  },
  tip_rate_pct: () =>
    "This shows tips as a percent of this row's net sales.",
  refund_rate_pct: () =>
    "This shows refunds as a percent of this row's gross sales.",
  payment_within_employee_pct: () =>
    "This shows what percent of this employee's net sales used this payment type.",
  employee_within_payment_pct: () =>
    "This shows this employee's share of net sales for this payment type.",
  baseline_key: (mixMeta) => {
    const primary = (mixMeta?.primary_mix_baseline || "").toLowerCase();
    if (primary && ["all", "day", "employee"].includes(primary)) {
      return primary;
    }
    const groupBy = (mixMeta?.group_by || "").toLowerCase();
    return GROUP_BASELINES[groupBy] || "all";
  },
};

export const TOTAL_SHEET_TOOLTIPS = {
  start_date: COMMON_FILTER_TOOLTIPS.start_date,
  end_date: COMMON_FILTER_TOOLTIPS.end_date,
  quick_pick: "Fast date ranges to fill Start and End dates.",
  summary_tab: "Shows totals and high-level breakdowns for the date range.",
  detail_export_tab: "Shows detailed rows for export and deeper review.",
  export_pdf: "Downloads this Summary as a PDF file.",
  total_sales_adjustments:
    "Total sales after refunds and discounts in this period.",
  total_income: "Total income recognized for this period.",
  total_payments: "Total money collected by payment methods in this period.",
  net_sales: "Sales after discounts and refunds, before tax.",
  refunds: "Money returned to customers in this period.",
  discounts: "Price reductions given on tickets in this period.",
  taxes: "Total tax collected in this period.",
  payments: "How customers paid (cash, debit, house, etc.).",
  percent_value: "This percent shows the share of the total for this section.",
  employees: "Employee totals for tickets, services, and tips in this period.",
  employee_tickets: "Number of tickets closed in this period.",
  employee_services: "Service sales credited to this employee.",
  employee_tips: "Tips credited to this employee.",
};

export const SERVICE_ANALYTICS_TOOLTIPS = {
  start_date: COMMON_FILTER_TOOLTIPS.start_date,
  end_date: COMMON_FILTER_TOOLTIPS.end_date,
  outlier_cap_hours:
    "Caps unusually long services so averages stay realistic.",
  employee: COMMON_FILTER_TOOLTIPS.employee,
  service_category: "Filter results to services in a specific category.",
  group_by: "Choose how results are grouped in the table.",
  duration_mode:
    "Choose how time is counted when tickets have multiple services.",
  include_refunds: COMMON_FILTER_TOOLTIPS.include_refunds,
  include_discounts: COMMON_FILTER_TOOLTIPS.include_discounts,
  clear: "Resets all filters to their defaults.",
  run_report: "Refreshes results using the selected filters.",
  tab_overview: "High-level summary of service sales and activity.",
  tab_mix_by_employee: "Shows each employee's share of service sales and quantity.",
  tab_service_performance: "Shows revenue, quantity, and time metrics by service.",
  tab_top_services: "Shows best-selling services by sales or quantity.",
  tab_clients_by_service: "Shows which clients bought which services.",
  tab_retention: "Shows repeat client behavior by service.",
  tab_addons_combos: "Shows services commonly sold together.",
  tab_discounts: "Shows discount impact by service or category.",
  tab_refunds: "Shows refund impact by service or category.",
  tab_capacity: "Shows time usage and capacity indicators from service durations.",
  column_service: "The service name.",
  column_qty: "How many times this service was sold.",
  column_gross: "Sales before discounts and refunds.",
  column_discount: "Total discount amount applied.",
  column_net: "Sales after discounts (and refunds if included).",
  column_percent_net: "This service's share of total net sales in the table.",
  column_percent_qty: "This service's share of total quantity in the table.",
  column_avg_duration:
    "Average service time (outliers capped by the selected limit).",
  column_total_duration: "Total time spent on this service in the period.",
};

export const CATEGORY_ANALYTICS_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  outlier_cap_hours: "Caps unusually long services so averages stay realistic.",
  duration_mode: "Choose how time is counted when tickets have multiple services.",
  time_bucket: "Sets the time bucket for the mix trend.",
  mix_share: "Switches between net sales share and services sold share.",
  clear: "Resets all filters to their defaults.",
  run_report: "Refreshes results using the selected filters.",
  tab_categoryTotals: "Totals by category for the selected range.",
  tab_categoryMix: "Shows category share over time.",
  tab_categoryStaffing: "Estimated hours and revenue per hour by category.",
  tab_categoryInsights: "Growth, concentration, and seasonality highlights.",
  column_category: "Service category name.",
  column_services_sold: "Number of services sold in this category.",
  column_gross_sales: "Sales before discounts and refunds.",
  column_discounts: "Total discounts for this category.",
  column_refunds: "Total refunds for this category.",
  column_net_sales: "Sales after discounts and refunds.",
  column_share_pct: "Share of total net sales in this table.",
  column_avg_net_per_service: "Average net sales per service.",
  column_bucket: "Time bucket for the mix trend.",
  column_total_net_sales: "Total net sales in this bucket.",
  column_top_categories: "Top categories and their share in this bucket.",
  column_est_labor_hours: "Estimated labor hours for this category.",
  column_revenue_per_labor_hour: "Net sales per labor hour.",
  column_top_employees: "Top employees by net sales in this category.",
  column_prev_net: "Net sales in the prior period.",
  column_change_net: "Change in net sales vs the prior period.",
  column_change_pct: "Percent change vs the prior period.",
  column_month: "Month in the last 12 months.",
  column_top_category: "Top category for the month.",
  column_top_categories_compact: "Leading categories and their share.",
};

export const CLIENT_ANALYTICS_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  group_by: "Sets the time bucket for trend rows.",
  active_in_range: "Limits bands to clients active in the date range.",
  rebooking_window: "Days after a visit to count a rebooking.",
  tab_overview: "Summary of client activity and spend.",
  tab_topClients: "Ranks clients by revenue, tips, or visit frequency.",
  tab_newVsReturning: "Compares new vs returning client trends.",
  tab_ltvBands: "Groups clients by lifetime value.",
  tab_rfmSegments: "Segments clients by recency, frequency, and spend.",
  tab_atRiskList: "List of clients who have not visited recently.",
  tab_rebookingRate: "Shows how often clients rebook within the window.",
  tab_cohortRetention: "Tracks retention by first visit month.",
  tab_targetLists: "Links to ready-to-use client lists.",
  top_clients_metric: "Metric used to rank clients.",
  top_clients_n: "Number of clients to include in the ranking.",
  top_clients_sort_order: "Sort order for the ranking.",
  column_period_start: "Start date for the period bucket.",
  column_new_clients: "New clients in the period.",
  column_returning_clients: "Returning clients in the period.",
  column_revenue_new_net: "Net revenue from new clients.",
  column_revenue_returning_net: "Net revenue from returning clients.",
  column_rank: "Rank based on the selected metric.",
  column_top_metric: "Metric total for the ranking.",
  column_ticket_count: "Number of tickets in the date range.",
  column_tip_count: "Number of tip records in the date range.",
  column_avg_spend_visit: "Average spend per visit.",
  column_band: "Lifetime value band.",
  column_clients: "Number of clients.",
  column_total_lifetime_net: "Total lifetime net sales.",
  column_avg_visits_lifetime: "Average visits per client lifetime.",
  column_last_visit: "Most recent visit date.",
  column_segment: "RFM segment label.",
  column_avg_recency: "Average days since last visit.",
  column_avg_frequency: "Average visits in the last 180 days.",
  column_avg_monetary: "Average net spend in the last 180 days.",
  column_action: "Open the client list for this segment.",
  column_client: "Client name.",
  column_recency_days: "Days since last visit.",
  column_frequency_180d: "Visits in the last 180 days.",
  column_monetary_180d: "Net spend in the last 180 days.",
  column_phone: "Phone on file.",
  column_email: "Email on file.",
  column_last_service: "Most recent service.",
  column_last_employee: "Most recent employee.",
  column_employee: "Employee name.",
  column_lifetime_net: "Lifetime net spend.",
  column_visits_12m: "Visits in the last 12 months.",
  column_opportunities: "Eligible visits in the window.",
  column_rebooked: "Visits that rebooked in the window.",
  column_rebook_rate: "Share of opportunities that rebooked.",
};

export const GIFT_CARD_ANALYTICS_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  as_of_date: "Balances are calculated as of this date.",
  client_search: COMMON_FILTER_TOOLTIPS.search,
  min_amount: "Minimum issued amount to include.",
  max_amount: "Maximum issued amount to include.",
  min_balance: "Minimum balance to include.",
  max_balance: "Maximum balance to include.",
  include_anonymous: "Includes anonymous buyers in totals and rows.",
  group_by: "Choose whether balances are grouped by gift card or client.",
  report_scope: "Tracks gift card sales, usage, and remaining balances.",
  clear: "Resets all filters to their defaults.",
  run_report: "Refreshes results using the selected filters.",
  tab_giftCardsSold: "Gift cards issued in the date range.",
  tab_balancesAsOf: "Balances as of a specific date.",
  tab_clientsWithBalance: "Clients with a balance as of the end date.",
  tab_salesRedemptions: "Issued vs redeemed totals for the range.",
  tab_liabilitySummary: "Outstanding gift card liability as of the date.",
  column_issued_date: "Date the gift card was issued.",
  column_code: "Gift card code or ID.",
  column_client: "Client name on the card.",
  column_amount: "Value issued on the gift card.",
  column_sold_by: "Employee who sold the gift card.",
  column_balance: "Remaining balance as of the date.",
  column_last_activity: "Most recent gift card activity.",
  column_gift_cards_count: "Number of gift cards with a balance.",
  column_total_balance: "Total balance as of the date.",
};

export const DAILY_CLOSING_CASH_TOOLTIPS = {
  work_date: "Date for the daily closing report (salon timezone).",
  history_start: COMMON_FILTER_TOOLTIPS.start_date,
  history_end: COMMON_FILTER_TOOLTIPS.end_date,
  load_from_tickets: "Prefills totals from tickets for this date.",
  save_submit: "Saves the daily closing report for this date.",
  sales_revenue: "Enter or review the day's sales and payment totals.",
  bank_card_total: "Total bank card payments, including tips.",
  cash_on_hand: "Cash counted after removing the beginning cash float.",
  tips_non_exempt: "Tips from regular taxable tickets. Filled from tickets.",
  tips_tax_exempt: "Tips from tax-exempt tickets. Filled from tickets.",
  gift_cards_sold: "Total value of gift cards sold that day.",
  paid_by_gift_card: "Total ticket payments made with gift cards.",
  cash_withdrawals: "Cash taken out for staff tips or bonuses.",
  cash_tip_withdrawals: "Cash tips paid out to technicians.",
  cash_bonus_withdrawals: "Cash bonuses paid out to technicians.",
  cash_total: "Cash left after tip and bonus withdrawals.",
  cash_expenses: "Cash paid out for business expenses.",
  add_expense: "Adds a cash expense row.",
  expense_category: "Type of cash expense.",
  expense_amount: "Cash amount paid out for this expense.",
  expense_description: "Short note about why the cash was paid out.",
  expense_receipt: "Upload a receipt image or PDF for this expense.",
  total_cash_expenses: "All cash expense rows added together.",
  notes: "Optional notes about this daily closing.",
  reconciliation: "Compares this closing total with the Total Sheet.",
  total_sales: "Sales total calculated from this closing form.",
  total_sheet_grand_total: "Grand total from the Total Sheet for this date.",
  difference: "Difference between this closing and the Total Sheet.",
  expected_cash_remaining: "Cash expected after withdrawals and expenses.",
  history: "Find and reopen saved daily closing reports.",
  refresh_history: "Reloads saved reports for the selected date range.",
};

export const CASH_BALANCE_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  quick_pick: "Quick ranges to set the start and end dates.",
  export_csv: "Downloads the report rows as a CSV file.",
  report_scope: "Tracks cash in, cash out, and expected cash on hand.",
  column_date: "Date for the cash activity row.",
  column_total_sales: "Total sales for the date.",
  column_cash_revenue: "Cash collected for the date.",
  column_cash_expense: "Cash paid out for the date.",
  column_category: "Cash activity category.",
  column_notes: "Notes recorded for the date.",
};

export const TAX_LIABILITY_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  show_tax_exempt: "Shows sales that are taxable vs non-taxable for the period.",
  show_taxable: "Includes taxable items in totals and rows.",
  pay_by_cash: "Limits results to items paid by cash.",
  run_detail: "Runs the detail report with the current filters.",
  run_summary: "Runs the summary report with the current filters.",
  column_date: "Ticket close date in salon timezone.",
  column_employee: "Employee credited for the item.",
  column_client: "Client name on the ticket.",
  column_service: "Service or item name.",
  column_price: "Price before discounts.",
  column_discount: "Discount amount applied.",
  column_taxable_total: "Taxable portion of the item.",
  column_nontaxable_total: "Non-taxable portion of the item.",
  column_total: "Total after discounts.",
  column_tax_percent: "Tax rate applied to the item.",
  column_tax_amount: "Tax collected for the item.",
};

export const SALES_AND_TICKETS_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  group_by: "Choose day or week buckets for trend tables.",
  heatmap_bucket: "Sets the time bucket size for the heatmap.",
  run_report: "Refreshes results using the selected filters.",
  export_zip: "Downloads the report tables as a ZIP file.",
  section_ticket_volume: "Ticket counts by period.",
  section_ticket_types: "Ticket mix by item type.",
  section_cancellations: "Cancelled vs no-show appointment counts.",
  section_booking_sources: "Appointments grouped by booking source.",
  section_heatmap: "Appointment counts by weekday and time of day.",
  section_conversion: "Shows how often appointments lead to closed tickets.",
  column_period: "Day or week bucket in salon timezone.",
  column_closed_tickets: "Tickets closed in the period.",
  column_refunded_tickets: "Tickets marked as refunds.",
  column_ticket_type: "Ticket mix category.",
  column_ticket_count: "Number of tickets in the category.",
  column_cancelled: "Cancelled appointments (no-shows excluded).",
  column_no_show: "Appointments marked as no-show.",
  column_timing_bucket: "Time before start when cancelled.",
  column_booking_source: "Appointment booking source.",
  column_appointments: "Number of appointments in the period.",
  column_served: "Appointments that were served.",
  column_with_closed_ticket: "Served appointments with closed tickets.",
  column_conversion_rate: "Share of served appointments with closed tickets.",
  column_weekday: "Weekday of the appointment start time.",
  column_heatmap_bucket: "Appointments in this time bucket.",
};

export const STAFF_PERFORMANCE_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  employees: COMMON_FILTER_TOOLTIPS.employee,
  rebook_window: "Days after a visit to count a rebooking.",
  hours_basis: "Choose whether hours come from time clock or booked time.",
  column_staff_name: "Staff member name.",
  column_available_hours: "Scheduled hours in the date range.",
  column_booked_hours: "Non-cancelled appointment hours.",
  column_clocked_hours: "Time clock hours worked.",
  column_utilization_pct: "Percent of available hours that were booked.",
  column_net_sales: "Net sales credited to this staff member.",
  column_ticket_count: "Tickets credited to this staff member.",
  column_tips_total: "Tips credited to this staff member.",
  column_tip_per_ticket: "Average tips per ticket.",
  column_tip_rate_pct: "Tips as a share of net sales.",
  column_refunds_total: "Refunds credited to this staff member.",
  column_refund_rate_pct: "Refunds as a share of net sales.",
  column_sales_per_hour: "Net sales per hour worked.",
  column_served_clients: "Unique clients served in the range.",
  column_rebooked_clients: "Clients who rebooked within the window.",
  column_rebook_rate_pct: "Share of served clients who rebooked.",
};

export const BUG_REPORT_TOOLTIPS = {
  date_from: "Filters by the salon's local accounting date (timezone-aware).",
  date_to: "Filters by the salon's local accounting date (timezone-aware).",
  business: "Limits results to bug reports submitted under the selected business.",
  salon: "Limits results to bug reports submitted under the selected salon.",
  search: "Searches title, description, and page URL.",
  status: "Filter by the bug's workflow status.",
  severity: "Use severity to prioritize triage (Critical/High first).",
  category: "Category helps group issues by feature area.",
  column_created: "Shown as the salon's local date.",
  column_business_salon: "Where the issue occurred.",
  column_severity: "Reported severity level.",
  column_status: "Current triage status.",
  column_reporter: "User who submitted the report.",
  column_page: "Page URL/path captured at submission.",
  column_screenshot: "Opens the attached screenshot if available.",
};

export const LIVE_DASHBOARD_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  scope:
    "Run the dashboard across all salons, one business, or one salon. Scope controls what is aggregated.",
  business: "Required in Business scope. Optional narrowing when scope is All Salons.",
  salon:
    "Required in Salon scope. Optional narrowing when Business scope is selected.",
  include_business_salon_cols:
    "Shows Business and Salon columns for top service rows in broad scopes.",
  bucket: "Choose day bars or week bars for the revenue trend.",
  sales: "Net sales = subtotal minus discounts (ex-tax) for tickets in the selected range.",
  sales_change: "Percent change in sales vs the immediately previous date range of equal length.",
  avg_ticket: "Net sales divided by number of tickets in the selected range.",
  revenue_per_booked_minute:
    "Net sales divided by booked appointment minutes for active staff in the selected range.",
  rebooking_rate:
    "Share of served clients who book another appointment within the rebook window.",
  utilization:
    "Booked appointment hours divided by available schedule hours for active staff.",
  revenue_trend: "Revenue trend bars by salon-local day or week.",
  top_services: "Top service descriptions by distinct ticket count, then sales.",
};

export const GIFT_CARDS_CASH_DRAWER_TOOLTIPS = {
  ...COMMON_FILTER_TOOLTIPS,
  group_by: "Groups trend tables by day or week.",
  variance_flag_threshold: "Dollar threshold for flagging over or short days.",
  paid_io_spike_multiplier: "Multiplier used to flag paid in or paid out spikes.",
  salon_timezone: "All dates use the salon timezone.",
  section_gift_liability: "Gift card liability over the selected range.",
  section_redemption: "Gift card redemptions over the selected range.",
  section_cash_over_short: "Cash expected vs counted by day.",
  section_paid_in_out: "Paid in and paid out totals by day.",
  column_period: "Day or week bucket.",
  column_sold: "Gift card value issued.",
  column_redeemed: "Gift card value redeemed.",
  column_outstanding: "Sold minus redeemed for the period.",
  column_sold_count: "Number of gift cards sold.",
  column_redeemed_count: "Number of gift cards redeemed.",
  column_work_date: "Work date for the cash drawer.",
  column_expected_cash: "System expected cash for the day.",
  column_counted_cash: "Counted cash entered for the day.",
  column_over_short: "Counted cash minus expected cash.",
  column_flag: "Marks days that exceed the thresholds.",
  column_paid_in: "Total paid in for the day.",
  column_paid_out: "Total paid out for the day.",
  column_paid_in_flag: "Flags paid in spikes.",
  column_paid_out_flag: "Flags paid out spikes.",
};

export const MAINTENANCE_REPORT_TOOLTIPS = {
  start_date: "First day included (salon timezone).",
  end_date: "Last day included (salon timezone).",
  staff: "Filter to tasks assigned to one staff member.",
  category: "Filter to one maintenance category.",
  frequency_type: "Filter by recurrence frequency type.",
  status: "Filters computed status outcomes for expected occurrences.",
  task: "Filter to one maintenance task.",
  include_inactive: "Include inactive tasks in report calculations.",
  run_report: "Refreshes all report sections using the current filters.",
  reset: "Restores default filters (last 30 days, all filters).",
  completion_rate:
    "Completed expected occurrences divided by expected occurrences in range.",
  expected_occurrences:
    "Occurrences generated from task frequency rules in salon timezone.",
  missed_definition: "Expected occurrences with no completion in their reporting window.",
  on_time_late_definition:
    "On-time: completion at or before due timestamp. Late: after due timestamp.",
  consistency_score:
    "0-100 score based on weighted on-time rate across expected occurrences.",
  event_based:
    "Event-based frequencies can be excluded from expected/missed totals when no safe expected driver exists.",
  section_trends: "Daily/weekly status outcomes over the selected date range.",
  section_frequency: "Expected/completed/missed performance split by frequency type.",
  section_tasks: "Task-level leaderboard for reliability and overdue risk.",
  section_staff: "Assigned-staff performance against expected maintenance work.",
  section_day_of_week: "Completions by weekday in salon local time.",
  section_logs: "Audit log of completion events with computed status at completion time.",
  export_logs: "Downloads completion logs as CSV for this date range and filters.",
};

export const SUPERADMIN_MAINTENANCE_REPORT_TOOLTIPS = {
  ...MAINTENANCE_REPORT_TOOLTIPS,
  business:
    "Choose one business or leave blank for all businesses. You can also paste a business ID.",
  salon:
    "Choose one salon in the selected business, or leave as All Salons. Disabled in All Businesses scope.",
  business_only_inclusion:
    "When no salon is selected, business-only maintenance data (business_id with salon_id null) is included when present.",
  timezone_rule:
    "Salon-scoped records use salon timezone. Business-only records use business timezone, or UTC when business timezone is missing.",
  leaderboard_business:
    "Business-level rollup of expected, completed, on-time, overdue, and missed outcomes.",
  leaderboard_salon:
    "Salon-level rollup for the selected scope. Hidden when no salons are in scope.",
};

export const TRAINING_PROGRESS_TOOLTIPS = {
  card: "Track staff training completion, overdue items, and progress by role.",
  date_range: "Filters training activity and due dates using your salon timezone.",
  role: "Show training progress for one role.",
  staff: "Show training progress for one staff member.",
  status: "Filter by Not started, In progress, Completed, or Overdue.",
  include_inactive_staff: "Include staff marked inactive.",
  staff_with_assignments: "Staff who have training assigned in this range.",
  in_progress: "Staff who started training but have not finished.",
  completed_all: "Staff who completed all assigned training.",
  average_completion_pct: "Average completion across staff with assignments.",
  column_staff: "Staff member name.",
  column_role: "Staff role.",
  column_assigned: "Number of courses assigned.",
  column_completed: "Number of courses completed.",
  column_status: "Overall training status for this staff member.",
  completion_pct: "Completed items ÷ total assigned items.",
  overdue: "Past due and not completed.",
  last_activity: "Last time progress was recorded.",
  next_due: "The next due date for assigned training.",
};


